import { useEffect } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router';

import { useAuthData } from '~/core/shared/contexts/AuthContext';
import { isSafeInternalPath } from '~/core/shared/contexts/ReturnUrlContext';
import { useUserData } from '~/core/shared/contexts/UserContext';
import { useIsLoggedIn } from '~/core/shared/hooks/useIsLoggedIn';
import { decodeJwtSub } from '~/core/shared/utils/authentication';

/**
 * On the exited screen, sends the user back to the page they were on when their
 * session ended, once a session for the SAME user reappears (typically because
 * they logged back in in another tab, which the session store propagates here).
 *
 * The page and the owning user's id are read from the URL (?returnUrl=&owner=),
 * so a browser reload on the exited screen does not lose them.
 *
 * Two guards decide what happens when a session comes back:
 * - Owner: if a DIFFERENT (or unverifiable) user's session returns, we do not
 *   send them to the previous user's page; we drop the stale params instead.
 * - Two-factor: for the same user we hold until 2FA is either satisfied (token
 *   present) or known not to be required, so we never return to a page that
 *   would immediately fail with a "requires 2FA" error. The requirement mirrors
 *   the backend AssertTwoFactorAuthenticated rule.
 */
export const useRestoreReturnUrlOnReauth = (): void => {
  const isLoggedIn = useIsLoggedIn();
  const tokens = useAuthData();
  const { user } = useUserData();
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // twoFactorRequired on the user payload is the backend TwoFactorEnforcement
  // policy's output (2FA for every human except super admins).
  const requiresTwoFactor = !!user?.twoFactorRequired;

  // Safe to return once the 2FA token is present, or once we know (user loaded)
  // that 2FA is not required for this user.
  const twoFactorSatisfied =
    !!tokens?.twoFactorToken || (!!user && !requiresTwoFactor);

  useEffect(() => {
    // Keep the captured params untouched while logged out, so a later re-auth
    // can still use them.
    if (!isLoggedIn) return;

    const returnUrl = searchParams.get('returnUrl');
    const owner = searchParams.get('owner');

    if (!returnUrl) return;

    const currentSub = tokens?.jwt ? decodeJwtSub(tokens.jwt) : undefined;
    const isSameOwner =
      isSafeInternalPath(returnUrl) &&
      !!owner &&
      !!currentSub &&
      owner === currentSub;

    // A different or unverifiable user's session came back (a shared computer):
    // drop the stale returnUrl/owner by replacing with the param-stripped
    // current location, rather than leaving the previous user's page in the URL.
    if (!isSameOwner) {
      navigate(pathname, { replace: true });
      return;
    }

    // Same user: hold on the exited screen until 2FA is satisfied or not
    // required, so the returned-to page does not immediately 2FA-error.
    if (!twoFactorSatisfied) return;

    navigate(returnUrl, { replace: true });
  }, [
    isLoggedIn,
    tokens?.jwt,
    twoFactorSatisfied,
    pathname,
    searchParams,
    navigate,
  ]);
};
